import Head from 'next/head'
import { useCallback, useEffect } from 'react'
import { useRouter } from 'next/router'

// components
import TypeaheadVehicleSearch from '../../components/typeahead-vehicle-search'
import ZipCodeModal from '../../components/zip-code/modal'
import Loader from '../../components/loader'
import { Filters, Listing, useViewModel } from '../../containers/search'
import ScrollToTop from '../../components/scroll-to-top'

// ----------------------------------------------------------------------

export default function Search() {
  const {
    query,
    querySetters,
    isZipCodeModalOpen,
    setIsZipCodeModalOpen,
    mouseDownTarget,
    searchScroll,
    isFetching,
    lastVehicleRef,
  } = useViewModel()

  const { setSearchTerms, setLocation } = querySetters

  const router = useRouter()
  const keywordFromQuery =
    typeof router.query.searchTerms === 'string'
      ? router.query.searchTerms
      : ''

  useEffect(() => {
    if (!router.isReady) return
    if (!keywordFromQuery) return

    setSearchTerms(keywordFromQuery)
  }, [router.isReady, keywordFromQuery, setSearchTerms])

  const handleMouseDown = (event) => {
    event.persist()
    mouseDownTarget.current = event.target
  }

  const handleTypeaheadSubmit = useCallback(
    (searchTerms) => {
      setSearchTerms(searchTerms)
      searchScroll.current.scrollIntoView()
    },
    [setSearchTerms, searchScroll],
  )

  const handleLocationResolve = useCallback(
    ({ zipCode, latitude, longitude }) => {
      setLocation({ zipCode, latitude, longitude })
      setIsZipCodeModalOpen(false)
    },
    [setIsZipCodeModalOpen, setLocation],
  )

  return (
    <>
      <Head>
        <title>Buy A Used Car Safely Online At privauto | privauto</title>
        <meta
          name='description'
          content='Looking to buy a used car safely? DriversExchange.com is the best site to buy a car online. Browse used cars for sale near your and make an offer!'
        />
      </Head>

      {isZipCodeModalOpen && (
        <ZipCodeModal
          onClose={() => setIsZipCodeModalOpen(false)}
          onLocationResolve={handleLocationResolve}
        />
      )}

      <div className='wrapper' onMouseDown={handleMouseDown}>
        <div className='searchInput' ref={searchScroll}>
          <TypeaheadVehicleSearch
            color='secondary'
            aria-label='search make, model or any keywords'
            placeholder='Search make, model or any keywords'
            onSubmit={handleTypeaheadSubmit}
            defaultValue={keywordFromQuery}
            data-hj-allow
          />
        </div>

        <Filters
          query={query}
          totalCount={query.listing.totalCount}
          totalUnrestrictedCount={query.listing.totalUnrestrictedCount}
          mouseDownTarget={mouseDownTarget}
          {...querySetters}
        />

        <Listing
          data={query.listing.vehicles}
          updatedData={query.listing.additionalInfo}
          isLoading={isFetching}
          viewType={query.viewType}
          setViewType={querySetters.setViewType}
          lastVehicleRef={lastVehicleRef}
        />

        <div className='loader flex alignCenter justifyCenter' style={{ padding: '2rem 0' }}>
          {isFetching && <Loader width='40' height='40' />}
        </div>

        <ScrollToTop />
      </div>

      <style jsx>{`
        .wrapper {
          background: var(--body-bg);

          .searchInput {
            padding: 3rem 0 0;
            background: var(--primary-bg-color);
          }

          :global(.paging) {
            padding: 7.4rem 0 5.7rem;
          }

          :global(.topPaging .paging) {
            padding: 2.5rem 0 1.5rem;
          }
        }

        .notification-wrapper {
          display: flex;
          justify-content: center;
          max-width: 192rem;
          width: 100%;
          margin: 0 auto;
          padding: 2rem 4rem 1rem;
        }

        .loader {
          background: var(--secondary-input-bg);
        }

        @media (max-width: 1024px) {
          .wrapper {
            .searchInput {
              padding: 3rem 3rem 0;
            }

            :global(.paging) {
              padding: 2rem 0 1.5rem;
            }
          }

          .notification-wrapper {
            padding: 0.1rem 1.2rem 2rem;
          }
        }
      `}</style>
    </>
  )
}

Search.getInitialProps = async () => ({
  namespacesRequired: ['common'],
})
